import { getAdaptedDate } from 'utils/formatting';
import { dateTimeFormats } from 'locale';
import { getLocaleString } from 'locale/utils';
import { removeExtension } from 'utils/file';
import { Portfolio } from 'core/types/api/wms';

// --- Interfaces ---

interface DocumentMetadata {
    name: Record<string, string>;
    isActive: boolean;
    documentCategory: {
        name: Record<string, string>;
    };
}

interface RawDocument {
    id: string;
    contractId: string;
    validTill: string | number | Date;
    createDate: string;
    externalId: string;
    filename: string;
    originalFilename?: string;
    documentMetadata: DocumentMetadata;
}

export interface UnifiedDocument {
    id: string;
    name: string;
    type: string;
    category: string;
    document: string;
    businessUnit: string;
    productName?: string;
    portfolioName?: string;
    portfolioId?: string;
    createDate: string;
    documentSource: string;
    lpzCustomerId: string;
    productId: string;
}

// --- Helper Functions ---

export const getNames = (data: { name: string }[]): string[] => {
    const listOfNames = new Set<string>();

    data.forEach((val) => listOfNames.add(val.name));

    return [...listOfNames];
};

const getProduct = ({ contractId, portfolios }: { contractId?: string; portfolios?: Portfolio[] }): string => {
    const notAvailable = 'N/A';

    if (!contractId || !portfolios?.length) return notAvailable;

    const findPortfolio = portfolios.find(({ UId }) => UId === contractId);

    return findPortfolio ? findPortfolio.Name as string : notAvailable;
};

interface PortfolioState {
    fromDocs: string;
    currentPage?: number;
    currentTab?: string;
}

interface PortfolioResult {
    label: string;
    link: string;
    disabled: boolean;
    state?: PortfolioState;
}

const getPortfolio = ({
    contractId, portfolios, currentPage, currentTab,
}: {
    contractId?: string;
    portfolios?: Portfolio[];
    currentPage?: number;
    currentTab?: string;
}): PortfolioResult | any => {
    const notAvailable = { label: 'N/A', link: '', disabled: true };

    if (!contractId || !portfolios) return notAvailable;

    const findPortfolio = portfolios.find(({ UId }) => UId === contractId);

    if (!findPortfolio) return notAvailable;

    const { Name } = findPortfolio;

    return {
        Name,
        state: {
            fromDocs: 'documents',
            currentPage,
            currentTab,
        },
    };
};

const getUnifiedDocumentPortfolio = ({
    portfolioName, currentPage, currentTab, portfolioId, clientId,
}: {
    portfolioName?: string;
    currentPage?: number;
    currentTab?: string;
    portfolioId?: string;
    clientId?: string;
}): PortfolioResult => {
    if (!portfolioName) {
        return { label: 'N/A', link: '', disabled: true };
    }

    return {
        label: portfolioName,
        link: `/client/${clientId}/portfolios/${portfolioId}/tab/overview`,
        disabled: false,
        state: {
            fromDocs: 'documents',
            currentPage,
            currentTab,
        },
    };
};

// --- Main Adapters ---

export const adaptDocuments = (
    data: { results?: RawDocument[]; page?: number } | undefined,
    downloadDocument: (id: string) => void,
    t: (key: string) => string,
    i18n: { language: string },
    getFormattedDate: (date: any, options?: any) => string,
    portfolios: Portfolio[],
    tab: string,
) => (data?.results || [])
    .map((item) => {
        const {
            id,
            contractId,
            validTill,
            createDate,
            externalId,
            filename,
            originalFilename,
            documentMetadata: {
                name,
                isActive,
                documentCategory: { name: documentTypeName },
            },
        } = item;
        const { language } = i18n;

        return {
            id,
            name: removeExtension(filename),
            nameCol: {
                link: `/documents/${id}/owner/${contractId}`,
                value: filename,
                onDownload: downloadDocument,
            },
            valid: getFormattedDate(validTill, {
                hour: dateTimeFormats.TWO_DIGIT,
                minute: dateTimeFormats.TWO_DIGIT,
                second: dateTimeFormats.TWO_DIGIT,
            }),
            isActive: t(`advisoryDashboard.documents.${isActive ? 'yes' : 'no'}`),
            filename: originalFilename || '',
            category: getLocaleString(name, language) || '',
            type: getLocaleString(documentTypeName, language) || '',
            product: getProduct({ contractId, portfolios }),
            contactId: getPortfolio({
                contractId,
                portfolios,
                currentPage: data?.page,
                currentTab: tab,
            }),
            externalId,
            fullDate: createDate,
            createDate: getFormattedDate(getAdaptedDate(createDate, t)),
            icon: '',
        };
    });

export const adaptUnifiedDocuments = ({
    data, dfsClientId, downloadDocument, documentDownloadId,
}: {
    data?: UnifiedDocument[];
    dfsClientId?: string;
    downloadDocument: (...args: any[]) => void;
    documentDownloadId?: string;
}) => {
    if (!data) {
        return [];
    }

    return data.map(item => ({
        id: item.id,
        name: item.name,
        type: item.type,
        category: item.category,
        document: item.document,
        businessUnit: item.businessUnit,
        product: item.productName || 'N/A',
        portfolio: getUnifiedDocumentPortfolio({
            portfolioName: item.portfolioName,
            currentPage: (data as any)?.page, // Unified data structure seems to vary
            currentTab: 'personal',
            clientId: dfsClientId,
            portfolioId: item.portfolioId,
        }),
        createDate: item.createDate,
        nameCol: {
            inProgress: item?.id === documentDownloadId,
            link: '',
            value: item.document,
            onDownload: () => downloadDocument(
                item.id,
                item.documentSource,
                item.lpzCustomerId,
                item.name,
                item.productId,
            ),
        },
    }));
};
